import { SearchIcon } from "@chakra-ui/icons";
import KYDInput from "src/ui/KYDInput";
import { HStack, VStack, Image, Text } from "@chakra-ui/react";
import KYDSearchInput from "src/ui/KYDSearchInput";
import { KYDAdminEventSummary } from "@common/interfaces/KYDEvent";
import {
  useInfiniteHits,
  useInstantSearch,
  useSearchBox,
} from "react-instantsearch";
import { Controller, useFormContext } from "react-hook-form";
import { useEffect, useState } from "react";
import { KYDAdminFeed } from "@common/interfaces/KYDOrganization";
import { KYDEventListItemData } from "@common/interfaces/KYDCampaign";
import { formatEventDateWithYear } from "src/utils";

const EventList = ({
  eventData,
  blockIndex,
  eventIndex,
  isDisabled,
  timezone,
}: {
  eventData: KYDEventListItemData | null;
  blockIndex: number;
  eventIndex: number;
  isDisabled?: boolean;
  timezone: string;
}) => {
  const key = `events.${eventIndex}`;

  const { items: events } = useInfiniteHits<KYDAdminEventSummary>({
    escapeHTML: false,
    showPrevious: false,
  });
  const { status: searchStatus } = useInstantSearch();
  const { refine } = useSearchBox();
  const [searchQuery, setSearchQuery] = useState("");
  const { control, setValue } = useFormContext();

  const handleSelect = (
    item: KYDAdminEventSummary | KYDAdminFeed["external_events"][number]
  ) => {
    console.log("item selected", item);
    setValue(
      `blocks.${blockIndex}.${key}.event`,
      {
        id: item.id,
        name: "name" in item ? item.name : item.title,
        image: item.image,
        start_at: item.start_at,
        end_at: "end_at" in item ? item.end_at : "",
        link: item.link,
        venue: "venues" in item ? item.venue.name : item.venue_name,
        is_external: "is_external" in item ? item.is_external : false,
        timezone: item.timezone || timezone || "America/New_York",
      } as KYDEventListItemData["event"],
      {
        shouldDirty: true,
        shouldTouch: true,
      }
    );

    if ("name" in item) {
      setSearchQuery(item.name);
    } else {
      setSearchQuery(item.title);
    }
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    refine(query);
  };

  useEffect(() => {
    if (eventData?.event?.name) {
      setSearchQuery(eventData?.event?.name);
    }
  }, [eventData?.event?.name]);

  return (
    <VStack gap={3} w="100%">
      <KYDSearchInput
        leftIcon={<SearchIcon color="#969696" fontSize="12px" mt={1} />}
        placeholder="Search for events"
        items={events}
        maxHeight="300px"
        width="100%"
        searchField="name"
        value={searchQuery}
        onChange={handleSearch}
        loading={searchStatus === "loading"}
        onSelect={handleSelect}
        isDisabled={isDisabled}
        itemComponent={(item: KYDAdminEventSummary, index: number) => {
          return (
            <HStack
              key={`${item.id}-${index}`}
              py={2}
              px={3}
              borderBottom={
                index === events.length - 1 ? "none" : "1px solid #E2E8F0"
              }
              w="100%"
              alignItems="flex-start"
              justifyContent="flex-start"
            >
              <Image
                borderRadius="8px"
                width="70px"
                height="70px"
                objectFit="cover"
                src={item.image}
                alt="Event 2 Column"
              />
              <VStack
                w="100%"
                alignItems="flex-start"
                gap={1}
                justifyContent="flex-start"
              >
                <Text fontSize="16px" fontFamily="Helvetica">
                  {item.name}
                </Text>
                <Text fontSize="12px" color="#1A202CB2">
                  {formatEventDateWithYear(
                    item.start_at,
                    item.timezone
                  ) || "Date"}
                </Text>
              </VStack>
            </HStack>
          );
        }}
      />

      {eventData && (
        <>
          <Controller
            name={`blocks.${blockIndex}.events.${eventIndex}.subcopy`}
            control={control}
            disabled={isDisabled}
            render={({ field }) => (
              <KYDInput
                {...field}
                placeholder="Optional"
                label="Subcopy"
                w="100%"
              />
            )}
          />
          <Controller
            name={`blocks.${blockIndex}.events.${eventIndex}.event.link`}
            control={control}
            disabled={isDisabled}
            render={({ field }) => (
              <KYDInput
                {...field}
                defaultValue={field.value}
                placeholder="Add link to redirect when event is clicked"
                label="Link"
                w="100%"
              />
            )}
          />
        </>
      )}
    </VStack>
  );
};

export default EventList;
